<script>
import ControlBar from '@/components/layouts/dashboard/ControlBar';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import EmptyState from '@/components/ui/EmptyState';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import UserCard from '@/components/users/UserCard';
import debounce from 'lodash/debounce';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'Users',
  components: {
    ControlBar,
    DashboardLayout,
    EmptyState,
    SidebarLink,
    SidebarLinkGroup,
    UserCard
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('Users'),
      breadcrumbs: [{ text: this.$t('Users') }],
      canLoadMore: false,
      limit: 50,
      isLoadingUsers: false,
      offset: 0,
      search: '',
      statusCode: null,
      users: []
    };
  },
  created() {
    // Initial list of users
    this.fetchUsers('', this.limit, 0)
      .then(() => {
        this.statusCode = 200;
        this.$nextTick(() => this.$refs.search.focus());
      })
      .catch(err => this.statusCode = err.response.status);
  },
  mounted() {
    // Watch for infinite scrolling
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeRouteLeave(to, from, next) {
    // Stop watching for infinite scrolling
    window.removeEventListener('scroll', this.handleScroll);
    next();
  },
  methods: {
    // Fetches a list of users
    fetchUsers(search, limit, offset) {
      this.isLoadingUsers = true;
      return this.$http.get('/users', {
        params: {
          search,
          limit,
          offset,
          sortBy: 'role'
        }
      })
        .then(res => {
          this.canLoadMore = res.data.count > res.data.offset + res.data.limit;
          this.offset = res.data.offset;

          if (res.data.offset === 0) {
            this.users = res.data.users;
          } else {
            res.data.users.map(user => this.users.push(user));
          }

          return res;
        })
        .finally(() => this.isLoadingUsers = false);
    },

    // Refreshes the list as the user types a search
    handleSearch: debounce(function() {
      this.fetchUsers(this.search, this.limit, 0).catch(err => {
        this.$notify({
          type: 'error',
          title: this.$t('Error'),
          message: getErrorMessage(err.response.data.code)
        });
      });
    }, 250),

    // Loads more when scrolling reaches the bottom
    handleScroll: debounce(function() {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
      const windowHeight = window.innerHeight;
      const threshold = windowHeight * 4;

      if (scrollTop > scrollHeight - threshold) {
        this.loadMore();
      }
    }, 500, { maxWait: 100 }),

    // Loads the next batch of users
    loadMore() {
      if (this.canLoadMore) {
        this.fetchUsers(this.search, this.limit, this.offset + this.limit).catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        });
      }
    }
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" size="large">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Admin -->
      <sidebar-link-group :title="$t('Admin')">
        <sidebar-link :text="$t('New User')" :to="{ name: 'NewUser' }" icon="el-icon-fa-plus" />
      </sidebar-link-group>
    </template>

    <!-- Control bar -->
    <control-bar :collapsable="false">
      <template slot="primary">
        <el-input
          ref="search"
          v-model="search"
          :placeholder="$t('Search Users')"
          :prefix-icon="isLoadingUsers ? 'el-icon-fa-circle-notch el-icon-fa-spin' : 'el-icon-fa-search'"
          clearable
          @input="handleSearch"
        />
      </template>
      <template slot="secondary">
        <el-button type="primary" @click="$router.push({ name: 'NewUser' })">
          {{ $t('New User') }}
        </el-button>
      </template>
    </control-bar>

    <!-- User cards -->
    <div class="user-cards">
      <el-row :gutter="32">
        <user-card v-for="user in users" :key="user.id" :user="user" />
      </el-row>
    </div>

    <!-- Load more button -->
    <div v-if="canLoadMore" class="text-center">
      <el-button :loading="isLoadingUsers" @click="loadMore">
        <i class="el-icon-fa-arrow-down" />
      </el-button>
    </div>

    <!-- No results -->
    <empty-state
      v-if="search.length && !users.length"
      :title="$t('No Results')"
      class="empty-state"
    />
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

// No styles yet

</style>
